import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import type { RecurringPayment } from '@/lib/api';
import { monthlyEquivalent } from '@/lib/recurring/cadence';
import { hasConvertedAmount, recurringDisplayAmount } from '@/lib/recurring/money';
import { cn } from '@/lib/utils';

/**
 * The row's money block.
 *
 * A series priced in a foreign currency has a FIXED price abroad and a BRL value that moves with
 * the exchange rate every month, so the row shows both: the converted value — the number that
 * actually left the account and the only one that sums — with the origin price under it. When the
 * bank has not reported a conversion the BRL side does not exist; the row shows the origin price
 * and says so, and the totals leave it out (same rule as `hasReportingAmount` on Lançamentos).
 */
export function RecurringAmount({ item, className }: { item: RecurringPayment; className?: string }) {
  const formatCurrency = useFormatCurrency();
  const price = item.price;
  const foreign = price != null && price.currencyCode !== 'BRL';
  const converted = hasConvertedAmount(item);
  // A yearly series must carry its cadence in every branch, or an annual foreign subscription
  // reads like a monthly charge at twelve times the price.
  const perYear = item.cadence === 'Yearly' ? '/ano' : '';

  if (foreign && !converted) {
    return (
      <div className={cn('shrink-0 text-right', className)} data-testid="recurring-amount">
        <p className="font-mono text-sm text-text-primary tabular-nums">
          {formatCurrency(price.amount, { currency: price.currencyCode })}
          {perYear}
        </p>
        <p className="mt-0.5 text-3xs text-warning-text">sem conversão</p>
      </div>
    );
  }

  if (foreign) {
    return (
      <div className={cn('shrink-0 text-right', className)} data-testid="recurring-amount">
        <p className="font-mono text-sm text-text-primary tabular-nums">
          {formatCurrency(recurringDisplayAmount(item))}
          {perYear}
        </p>
        <p className="mt-0.5 font-mono text-3xs text-text-tertiary tabular-nums">
          {formatCurrency(price.amount, { currency: price.currencyCode })}
          {perYear}
        </p>
      </div>
    );
  }

  const amount = recurringDisplayAmount(item);
  if (item.cadence === 'Yearly') {
    return (
      <div className={cn('shrink-0 text-right', className)} data-testid="recurring-amount">
        <p className="font-mono text-sm text-text-primary tabular-nums">{formatCurrency(amount)}/ano</p>
        <p className="mt-0.5 font-mono text-3xs text-text-tertiary tabular-nums">
          ≈ {formatCurrency(monthlyEquivalent(amount, item.cadence))}/mês
        </p>
      </div>
    );
  }

  return (
    <p
      className={cn('shrink-0 font-mono text-sm text-text-primary tabular-nums', className)}
      data-testid="recurring-amount"
    >
      {formatCurrency(amount)}
    </p>
  );
}
